import React, { useEffect, useState, useCallback } from 'react';
import { css } from '@emotion/css';
import * as yup from 'yup';
import { FormikProps, withFormik } from 'formik';
import TemplateFormSectionModel from '../../../../shared/models/templating/api/TemplateFormSectionModel';
import ApplicationContainer from '../../../../shared/state/containers/ApplicationContainer';
import AppliedApplicationTemplateContainer from '../../../../shared/state/containers/AppliedApplicationTemplateContainer';
import TemplateFormFieldValueModel from '../../../../shared/models/templating/api/TemplateFormFieldValueModel';
import AppliedApplicationTemplateModel from '../../../../shared/models/templating/api/AppliedApplicationTemplateModel';
import TemplateFormFieldModel from '../../../../shared/models/templating/api/TemplateFormFieldModel';
import Loader from '../../../../shared/components/general/Loader';
import PanelHeaderContainer from '../../../../shared/components/structure/Panels/PanelHeaderContainer';
import Button from '../../../../shared/components/general/Button';
import AppliedApplicationTemplateSectionForm from './AppliedApplicationTemplateSectionForm';
import styled from '@emotion/styled';
import { breakpoint_small } from '../../../../shared/constants/breakpoints';
import TemplateSaveChangesModal from '../../../../shared/components/templating/TemplateSaveChangesModal';
import { sectionHasVisibleFields } from '../../../../shared/utils/sectionUtils';

const instructionsIcon = require('../../../../shared/content/images/info_icon.svg');

interface AppliedAppTemplatesManagerProps {
    applicationId: string
    templateSection: TemplateFormSectionModel
    appContainer: ApplicationContainer
    appliedAppTemplateContainer: AppliedApplicationTemplateContainer
    appliedAppValues: TemplateFormFieldValueModel[]
    appliedAppTemplate: AppliedApplicationTemplateModel
    onNext: (latestFormValues: TemplateFormFieldValueModel[]) => void
    onPrevious: () => void
    insertDynamicFormSection: (section: TemplateFormSectionModel) => void
    history: any
    currentSectionIndex?: number
    importedFormFields: TemplateFormFieldValueModel[]
    setImportedFormFields: (fields: TemplateFormFieldValueModel[]) => void
    calculateIncompleteSections: (templateFormFieldValues?: TemplateFormFieldValueModel[]) => void
    showAdminFields?: boolean
    onFieldValidationChange?: (fieldId: string, hasErrors: boolean) => void
}

interface AppliedAppTemplatesManagerFormData {
    templateFormValues: TemplateFormFieldValueModel[]
}

const InnerForm: React.FC<AppliedAppTemplatesManagerProps & FormikProps<AppliedAppTemplatesManagerFormData>> = (props) => {
    const [recentlyAddedModuleFormFields, setRecentlyAddedModuleFormFields] = useState<TemplateFormFieldModel[]>();
    const [showSaveChangesModal, setShowSaveChangesModal] = useState(false);
    const [fieldValidationErrors, setFieldValidationErrors] = useState<Record<string, boolean>>({});

    const requiredFields = props.templateSection?.templateFormFields?.filter(f => f.isRequired) ?? [];
    const combinedRequiredFields = [...requiredFields, ...(props.templateSection?.dynamicFormFields?.filter(f => f.isRequired) ?? [])];
    const completeFields = combinedRequiredFields.filter(f => {
        const matchingValue = props.values.templateFormValues?.find(v =>
            (v.templateFormFieldId == f.id)
        );

        if (!matchingValue) {
            return false;
        }

        const hasValidValue = matchingValue.value !== null &&
            matchingValue.value !== undefined &&
            matchingValue.value !== '' &&
            matchingValue.value.trim() !== '';

        return hasValidValue;
    });
    const hasValidationErrors = Object.values(fieldValidationErrors).some(hasError => hasError);

    const handleFieldValidationChange = useCallback((fieldId: string, hasErrors: boolean) => {
        setFieldValidationErrors(prev => ({
            ...prev,
            [fieldId]: hasErrors
        }));
        // Notify parent component of validation state change
        if (props.onFieldValidationChange) {
            props.onFieldValidationChange(fieldId, hasErrors);
        }
    }, [props.onFieldValidationChange]);
    // Check if section has visible fields on mount and when values change
    useEffect(() => {
        if (!props.templateSection) return;

        const hasVisibleFields = sectionHasVisibleFields(
            props.templateSection,
            props.showAdminFields ?? true,
            props.values.templateFormValues
        );

        // If no visible fields, automatically navigate to next section
        if (!hasVisibleFields) {
            // Small delay to ensure state is settled
            setTimeout(() => {
                props.onNext(props.values.templateFormValues);
            }, 0);
        }
    }, [props.templateSection, props.values.templateFormValues, props.showAdminFields]);

    useEffect(() => {
        const allSections = props.appliedAppTemplate?.templateForm?.templateFormSections ?? [];
        const allFields = allSections.flatMap(s => s.templateFormFields);
        if (props.importedFormFields.length > 0) {
            for (const templateFormField of props.importedFormFields) {
                const matchingFormField = allFields?.find(formField => formField.title === templateFormField.title);
                if (matchingFormField) {
                    onDynamicFormValueChanged(matchingFormField, templateFormField.value);
                };
            };
        };
        props.calculateIncompleteSections(props.importedFormFields && props.importedFormFields);
    }, [props.importedFormFields]);

    useEffect(() => {
        const recentlyAddedFields = props.templateSection?.templateFormFields?.filter(f => f.recentlyAdded) ?? [];
        for (const templateFormSection of props.appliedAppTemplate?.templateForm?.templateFormSections ?? []) {
            for (const templateFormField of templateFormSection?.templateFormFields) {
                if (!props.appliedAppTemplate?.templateFormFieldValues.find(templateFormFieldValue => templateFormFieldValue.title === templateFormField.title)) {
                    if (templateFormField.defaultValue) {
                        recentlyAddedFields.push({ ...templateFormField, recentlyAdded: true });
                    }
                }
            }
        }
        setRecentlyAddedModuleFormFields(recentlyAddedFields);
    }, []);

    useEffect(() => {
        if (props.appliedAppValues.length === 0) return;
        const updatedFieldValues = [...props.appliedAppValues];
        for (const templateFormSection of props.appliedAppTemplate?.templateForm?.templateFormSections ?? []) {
            for (const templateFormField of templateFormSection?.templateFormFields) {
                if (!props.appliedAppTemplate?.templateFormFieldValues.find(templateFormFieldValue => templateFormFieldValue.title === templateFormField.title)) {
                    updatedFieldValues.push({
                        value: templateFormField.defaultValue,
                        fieldType: templateFormField.fieldType,
                        tip: templateFormField.tip,
                        title: templateFormField.title,
                        label: templateFormField.label,
                        defaultValue: templateFormField.defaultValue,
                        associatedVariable: templateFormField.associatedVariable,
                        placeholder: templateFormField.placeholder,
                        templateFormFieldId: templateFormField.id
                    });
                }
            }
        }
        props.setFieldValue("templateFormValues", updatedFieldValues);
    }, [props.appliedAppValues]);

    const onDynamicFormValueChanged = (field: TemplateFormFieldModel, value: string) => {
        const templateValues = [...(props.values.templateFormValues ?? [])];
        // if we have the value, update it
        const existingField = templateValues.find(v => (field.name && v.dynamicFormFieldName == field.name) || (field.title && v.title == field.title))
        if (existingField != null) {
            const index = templateValues.findIndex(v => v.title == existingField.title);
            existingField.value = value;
            templateValues[index] = existingField;
        }
        // if we don't, add it
        else {
            const fieldValue: TemplateFormFieldValueModel = {
                value: value,
                fieldType: field.fieldType,
                tip: field.tip,
                title: field.title,
                label: field.label,
                defaultValue: field.defaultValue,
                associatedVariable: field.associatedVariable,
                placeholder: field.placeholder
            };
            if (field.name)
                fieldValue.dynamicFormFieldName = field.name;
            else
                fieldValue.templateFormFieldId = field.id;
            templateValues.push(fieldValue);
        }
        props.setFieldValue("templateFormValues", templateValues);
        props.calculateIncompleteSections(templateValues);
    }

    const isLoading = props.appliedAppTemplateContainer.state.isLoading || props.appliedAppTemplateContainer.state.isSaving;

    const handleSaveForm = (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }
        setShowSaveChangesModal(true);
    };

    const performSave = async () => {
        await props.appliedAppTemplateContainer.saveTemplateFields(
            props.applicationId,
            props.appliedAppTemplate.id,
            props.values.templateFormValues,
            {
                finalize: false,
                useModifiedFieldsOnly: true
            }
        );
    };

    const closeSaveChangesModal = () => {
        setShowSaveChangesModal(false);
    };

    const handleNextSection = (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }
        // Pass latest form values to parent for navigation
        props.onNext(props.values.templateFormValues);
    };

    // Check if the current section has visible fields
    const hasVisibleFields = props.templateSection
        ? sectionHasVisibleFields(
            props.templateSection,
            props.showAdminFields ?? true,
            props.values.templateFormValues
        )
        : false;

    // Don't render the form if there are no visible fields
    // The useEffect above will handle navigation
    if (!hasVisibleFields) {
        return <Loader />;
    }

    return (
        <>
            {
                props.appliedAppTemplateContainer.state.isLoading ?
                    <Loader /> :
                    <form onSubmit={handleNextSection}>
                        <PanelHeaderContainer>
                            <InstructionsIcon src={instructionsIcon} />
                            <InstructionsText>Complete the fields below</InstructionsText>
                            <div className={buttonContainerStyle}>
                                {/* These are the buttons in the top right of the Spark form */}
                                {(props.currentSectionIndex != null && props.currentSectionIndex > 0) &&
                                    <PreviousButton
                                        disabled={props.appliedAppTemplateContainer.state.isLoading}
                                        themes={["white-small"]}
                                        text="Previous"
                                        type="button"
                                        onClick={props.onPrevious}
                                    />
                                }
                                <SaveButton
                                    loading={isLoading}
                                    disabled={hasValidationErrors}
                                    themes={["secondary-small"]}
                                    text="Save"
                                    type="button"
                                    onClick={handleSaveForm}
                                />
                                <NextButton
                                    disabled={hasValidationErrors || (!isLoading && (props.appliedAppTemplate?.templateForm?.templateConfiguration?.dynamicFormUrl != null) && (combinedRequiredFields.length != completeFields.length))}
                                    loading={isLoading}
                                    themes={["primary-small"]}
                                    text="Next"
                                    type="button"
                                    onClick={handleNextSection}
                                />
                            </div>
                        </PanelHeaderContainer>
                        {props.templateSection &&
                            <AppliedApplicationTemplateSectionForm
                                onPrevious={props.onPrevious}
                                submitForm={props.submitForm}
                                combinedRequiredFields={combinedRequiredFields}
                                completeFields={completeFields}
                                appliedAppTemplateContainer={props.appliedAppTemplateContainer}
                                appliedAppTemplate={props.appliedAppTemplate}
                                key={props.templateSection?.name ?? props.templateSection?.id}
                                section={props.templateSection}
                                values={props.values.templateFormValues}
                                templateFormValues={props.values.templateFormValues}
                                applicationId={props.applicationId}
                                onFieldValueChange={onDynamicFormValueChanged}
                                recentlyAddedModuleFormFields={recentlyAddedModuleFormFields}
                                setFieldValue={props.setFieldValue}
                                showAdminFields={props.showAdminFields}
                                appContainer={props.appContainer}
                                onFieldValidationChange={handleFieldValidationChange}
                            />
                        }
                    </form>
            }
            <TemplateSaveChangesModal
                showSaveChangesModal={showSaveChangesModal}
                closeModal={closeSaveChangesModal}
                changedFields={props.appliedAppTemplateContainer.getChangedFieldsForDisplay(
                    props.appliedAppTemplate.id,
                    props.values.templateFormValues
                )}
                submitChanges={performSave}
                isLoading={props.appliedAppTemplateContainer.state.isSaving}
                hasErrors={false}
            />
        </>
    )
}

const PreviousButton = styled(Button)``;

const SaveButton = styled(Button)`
    margin-right: 0;
`;

const NextButton = styled(Button)`
    margin-left: 0;
`;

const InstructionsIcon = styled.img`
    width: 24px;
    height: 24px;
    margin: 0 8px 0 32px;
    ${breakpoint_small} {
        display: none;
    }
`;

const InstructionsText = styled.p`
    ${breakpoint_small} {
        display: none;
    }
`;

const buttonContainerStyle = css`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 16px;
    margin-left: auto;
    button {
        margin: 0 16px 0 0;
    }
    ${breakpoint_small} {
        margin-right: 0;
    }
`;

const UpdateAppliedAppTemplateForm = withFormik<AppliedAppTemplatesManagerProps, AppliedAppTemplatesManagerFormData>({
    mapPropsToValues: props => {
        return {
            templateFormValues: [...(props.appliedAppValues ?? [])]
        }
    },
    validationSchema: yup.object().shape({
    }),
    handleSubmit: async (values, { props, setFieldError }) => {
        // Pass latest form values up to parent before navigating
        props.onNext(values.templateFormValues);
    }
})(InnerForm);


export default UpdateAppliedAppTemplateForm;